import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Loader2, ShieldCheck, Upload, CheckCircle2, AlertCircle, Clock,
  Camera, Lock, Eye, FileText, ChevronRight, Download, Bell,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getMyKycStatus, recordHelperDoc, submitKycForReview, getDocSignedUrl, updateKycReminderPrefs } from "@/lib/helper-kyc.functions";

export const Route = createFileRoute("/_authenticated/helper/kyc")({
  head: () => ({ meta: [{ title: "Verify your account — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: KycPage,
});

const DOC_META: Record<string, { label: string; help: string; camera?: boolean }> = {
  id_front: { label: "Photo ID (front)", help: "Passport photo page, UK driving licence, or BRP.", camera: true },
  id_back:  { label: "Photo ID (back)", help: "The back of your driving licence or BRP. Skip for passports.", camera: true },
  selfie:   { label: "Selfie holding your ID", help: "Hold your ID next to your face in good light.", camera: true },
  address_proof: { label: "Proof of address", help: "Utility bill, council tax, or bank statement from the last 3 months." },
  right_to_work: { label: "Right to work in the UK", help: "Share Code from gov.uk, BRP, or passport with visa. Required by UK law." },
  dbs: { label: "Basic DBS certificate", help: "Get one in 14 days at gov.uk/request-copy-criminal-record. Required for child-facing work." },
  insurance: { label: "Insurance certificate", help: "Trade-specific insurance (electricians, plumbers, etc.)." },
  trade_certificate: { label: "Trade certificate", help: "Gas Safe, NICEIC, City & Guilds — anything that proves your trade." },
};
const REQUIRED_ORDER = ["id_front", "id_back", "selfie", "address_proof", "right_to_work"];
const OPTIONAL_ORDER = ["dbs", "insurance", "trade_certificate"];
const EXPIRY_REQUIRED = new Set(["right_to_work", "dbs", "insurance", "trade_certificate"]);
const ALL_TYPES = [...REQUIRED_ORDER, ...OPTIONAL_ORDER];

type Doc = { id: string; doc_type: string; status: string; original_filename: string | null; reviewer_notes: string | null; created_at: string; expires_at: string | null; scan_status?: string | null; decision_code?: string | null };
type Profile = { verification_status: string; kyc_submitted_at: string | null; kyc_reviewed_at: string | null; kyc_reviewer_notes: string | null } | null;
type Prefs = { kyc_reminder_lead_days: number[]; share_verification_status_publicly: boolean } | null;
const LEAD_OPTIONS = [60, 45, 30, 14, 7, 3, 1];

function KycPage() {
  const navigate = useNavigate();
  const fetchStatus = useServerFn(getMyKycStatus);
  const recordDoc = useServerFn(recordHelperDoc);
  const submitForReview = useServerFn(submitKycForReview);
  const signDoc = useServerFn(getDocSignedUrl);
  const savePrefs = useServerFn(updateKycReminderPrefs);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [profile, setProfile] = useState<Profile>(null);
  const [prefs, setPrefs] = useState<Prefs>(null);
  const [required, setRequired] = useState<string[]>([]);
  const [uploadingType, setUploadingType] = useState<string | null>(null);
  const [expiryByType, setExpiryByType] = useState<Record<string, string>>({});

  async function refresh() {
    const res = await fetchStatus();
    setDocs(res.docs as Doc[]);
    setProfile(res.profile as Profile);
    setPrefs((res.prefs ?? { kyc_reminder_lead_days: [30, 7, 1], share_verification_status_publicly: true }) as Prefs);
    setRequired([...res.requiredTypes]);
  }

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      if (!data.user) { navigate({ to: "/auth", search: { redirect: "/helper/kyc" } }); return; }
      setAuthed(true);
      try { await refresh(); } finally { setLoading(false); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUpload(docType: string, file: File) {
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    if (!/^(image\/|application\/pdf)/.test(file.type)) { toast.error("Use JPG, PNG or PDF"); return; }
    if (EXPIRY_REQUIRED.has(docType) && !expiryByType[docType]) {
      toast.error("Please enter the expiry date first"); return;
    }
    let expiresAt: string | undefined;
    if (EXPIRY_REQUIRED.has(docType) && expiryByType[docType]) {
      const d = new Date(expiryByType[docType] + "T00:00:00Z");
      if (Number.isNaN(d.getTime())) { toast.error("Invalid expiry date"); return; }
      if (d.getTime() < Date.now()) { toast.error("Expiry date is in the past"); return; }
      expiresAt = d.toISOString();
    }
    setUploadingType(docType);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id;
      if (!userId) throw new Error("not_signed_in");
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "bin";
      const path = `${userId}/${docType}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("helper-kyc").upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw new Error(upErr.message);
      await recordDoc({ data: { docType: docType as "id_front", storagePath: path, originalFilename: file.name, mimeType: file.type, sizeBytes: file.size, expiresAt } });
      toast.success("Uploaded — looks good.");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally { setUploadingType(null); }
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await submitForReview();
      toast.success("Submitted for review — we'll email you within 24 hours.");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not submit");
    } finally { setSubmitting(false); }
  }

  async function handleDownload(docId: string) {
    try {
      const { url } = await signDoc({ data: { docId, download: true } });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not download");
    }
  }

  async function toggleLead(day: number) {
    const cur = new Set(prefs?.kyc_reminder_lead_days ?? [30, 7, 1]);
    if (cur.has(day)) cur.delete(day); else cur.add(day);
    const next = [...cur];
    if (next.length === 0) { toast.error("Keep at least one reminder"); return; }
    try {
      const res = await savePrefs({ data: { leadDays: next, sharePublicly: prefs?.share_verification_status_publicly ?? true } });
      setPrefs((p) => ({ kyc_reminder_lead_days: res.leadDays, share_verification_status_publicly: p?.share_verification_status_publicly ?? true }));
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not save"); }
  }

  async function toggleSharePublic(v: boolean) {
    try {
      await savePrefs({ data: { leadDays: prefs?.kyc_reminder_lead_days ?? [30,7,1], sharePublicly: v } });
      setPrefs((p) => ({ kyc_reminder_lead_days: p?.kyc_reminder_lead_days ?? [30,7,1], share_verification_status_publicly: v }));
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not save"); }
  }

  const status = profile?.verification_status ?? "unverified";
  const haveSet = new Set(docs.map((d) => d.doc_type));
  const missingRequired = required.filter((t) => !haveSet.has(t));
  const canSubmit = missingRequired.length === 0 && status !== "pending" && status !== "verified";
  const requiredDone = required.filter((t) => haveSet.has(t)).length;
  const requiredTotal = required.length || 1;
  const percent = Math.round((requiredDone / requiredTotal) * 100);

  if (!authed || loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main id="main-content" className="grid flex-1 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-primary">
          <ShieldCheck className="h-3.5 w-3.5" /> Helper verification · Step 2 of 4
        </div>
        <h1 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Verify your account</h1>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base">A quick check keeps the marketplace safe. Reviewed within 24 hours on weekdays.</p>

        {/* Progress bar */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-4 shadow-card sm:p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium">Required documents</p>
            <span className="font-display text-sm font-bold tabular-nums">{requiredDone}/{requiredTotal} · {percent}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500 transition-all" style={{ width: `${percent}%` }} />
          </div>
        </div>

        <StatusBanner status={status} profile={profile} />

        {/* Why we ask */}
        <details className="mt-4 rounded-2xl border border-border bg-card p-4 shadow-card open:shadow-elegant sm:p-5">
          <summary className="cursor-pointer list-none">
            <span className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-2 font-medium">
                <Eye className="h-4 w-4 text-primary" /> Why we need this & how we protect it
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform [details[open]_&]:rotate-90" />
            </span>
          </summary>
          <div className="mt-3 grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
            <div><Lock className="mb-1 h-4 w-4 text-primary" /><p className="font-medium text-foreground">Encrypted at rest</p><p className="text-xs">Documents are stored in a private bucket — only reviewers can see them.</p></div>
            <div><ShieldCheck className="mb-1 h-4 w-4 text-primary" /><p className="font-medium text-foreground">Trusted marketplace</p><p className="text-xs">Verified Helpers get a badge customers can see on your profile.</p></div>
            <div><FileText className="mb-1 h-4 w-4 text-primary" /><p className="font-medium text-foreground">UK law compliance</p><p className="text-xs">Right-to-work checks are a legal requirement for paid work in the UK.</p></div>
          </div>
        </details>

        {/* Reminder preferences */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-4 shadow-card sm:p-5">
          <div className="flex items-start gap-3">
            <Bell className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="flex-1">
              <p className="font-medium">Expiry reminders</p>
              <p className="mt-0.5 text-xs text-muted-foreground">We'll remind you before Right to Work, DBS, insurance or trade certificates expire so your profile stays verified. Unsubscribe any time from email footer.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {LEAD_OPTIONS.map((d) => {
                  const on = (prefs?.kyc_reminder_lead_days ?? [30,7,1]).includes(d);
                  return (
                    <button key={d} type="button" onClick={() => toggleLead(d)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${on ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground hover:border-primary/40"}`}>
                      {d} day{d === 1 ? "" : "s"} before
                    </button>
                  );
                })}
              </div>
              <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={prefs?.share_verification_status_publicly ?? true} onChange={(e) => toggleSharePublic(e.target.checked)} className="h-3.5 w-3.5 rounded border-input" />
                Show my Right to Work and DBS status on my public profile so customers can filter for it.
              </label>
            </div>
          </div>
        </div>

        {/* Required documents */}
        <h2 className="mt-8 font-display text-lg font-semibold">Required</h2>
        <ol className="mt-3 space-y-3">
          {REQUIRED_ORDER.filter((t) => required.includes(t)).map((t) => (
            <DocRow key={t} type={t} docs={docs} uploadingType={uploadingType} onUpload={handleUpload} onDownload={handleDownload} isReq expiryValue={expiryByType[t] ?? ""} onExpiryChange={(v) => setExpiryByType((s) => ({ ...s, [t]: v }))} />
          ))}
        </ol>

        {/* Optional / accelerators */}
        <h2 className="mt-8 font-display text-lg font-semibold">Boost your trust score <span className="ml-1 text-xs font-normal text-muted-foreground">(optional)</span></h2>
        <p className="mt-1 text-xs text-muted-foreground">Adding these unlocks higher-paying jobs and the Trusted Pro badge.</p>
        <ol className="mt-3 space-y-3">
          {OPTIONAL_ORDER.map((t) => (
            <DocRow key={t} type={t} docs={docs} uploadingType={uploadingType} onUpload={handleUpload} onDownload={handleDownload} isReq={false} expiryValue={expiryByType[t] ?? ""} onExpiryChange={(v) => setExpiryByType((s) => ({ ...s, [t]: v }))} />
          ))}
        </ol>

        {/* Submit card */}
        <div className="mt-8 grid grid-cols-1 gap-3 rounded-2xl border border-border bg-card p-5 shadow-card sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
          <div className="text-sm">
            {missingRequired.length === 0
              ? <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="h-4 w-4" /> All required documents uploaded. Ready to submit.</span>
              : <span className="text-muted-foreground">Still required: {missingRequired.map((t) => DOC_META[t]?.label).join(", ")}</span>}
          </div>
          <div className="flex gap-2 sm:justify-end">
            <Button variant="outline" asChild className="flex-1 sm:flex-none"><Link to="/dashboard">Back</Link></Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || submitting} className="flex-1 sm:flex-none">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {status === "pending" ? "In review" : status === "verified" ? "Verified" : "Submit for review"}
            </Button>
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Reviewed by a real person, usually within 24 hours, and we'll email you the result.
        </p>
      </main>
      <Footer />
    </div>
  );
}

function DocRow({
  type, docs, uploadingType, onUpload, onDownload, isReq, expiryValue, onExpiryChange,
}: {
  type: string;
  docs: Doc[];
  uploadingType: string | null;
  onUpload: (t: string, f: File) => void;
  onDownload: (docId: string) => void;
  isReq: boolean;
  expiryValue: string;
  onExpiryChange: (v: string) => void;
}) {
  const meta = DOC_META[type];
  const existing = docs.find((d) => d.doc_type === type);
  const needsExpiry = EXPIRY_REQUIRED.has(type);
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  return (
    <li className="rounded-2xl border border-border bg-card p-4 shadow-card sm:p-5">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 font-semibold">
            {meta?.label}
            {isReq
              ? <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-destructive">Required</span>
              : <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">Boosts trust</span>}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{meta?.help}</p>
          {existing ? (
            <p className="mt-2 inline-flex flex-wrap items-center gap-1.5 text-xs">
              {existing.status === "approved" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                : existing.status === "rejected" ? <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                : <Clock className="h-3.5 w-3.5 text-amber-500" />}
              <span className="truncate text-muted-foreground">{existing.original_filename ?? "Uploaded"} · <span className="capitalize">{existing.status}</span></span>
              {existing.expires_at && (
                <span className="text-muted-foreground">· expires {new Date(existing.expires_at).toLocaleDateString("en-GB")}</span>
              )}
              {existing.reviewer_notes && <span className="ml-1 text-destructive">— {existing.reviewer_notes}</span>}
            </p>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">JPG, PNG or PDF, max 10MB.</p>
          )}
          {needsExpiry && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Label htmlFor={`exp-${type}`} className="text-xs font-medium">Expiry date</Label>
              <input
                id={`exp-${type}`}
                type="date"
                min={minDate}
                value={expiryValue}
                onChange={(e) => onExpiryChange(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-xs"
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          {existing && (
            <button type="button" onClick={() => onDownload(existing.id)}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent"
              title="Download your submitted document">
              <Download className="h-3.5 w-3.5" /> Receipt
            </button>
          )}
          {meta?.camera && (
            <Label htmlFor={`cam-${type}`} className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent sm:hidden">
              <Camera className="h-3.5 w-3.5" /> Camera
            </Label>
          )}
          <Label htmlFor={`file-${type}`} className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent">
            {uploadingType === type ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {existing ? "Replace" : "Upload"}
          </Label>
          {meta?.camera && (
            <input
              id={`cam-${type}`}
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(type, f); e.target.value = ""; }}
            />
          )}
          <input
            id={`file-${type}`}
            type="file"
            accept="image/*,application/pdf"
            className="sr-only"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(type, f); e.target.value = ""; }}
          />
        </div>
      </div>
    </li>
  );
}

function StatusBanner({ status, profile }: { status: string; profile: Profile }) {
  const map: Record<string, { tone: string; title: string; body: string; icon: typeof CheckCircle2 }> = {
    unverified: { tone: "bg-muted text-foreground", title: "Not started", body: "Upload the required documents below to get verified.", icon: AlertCircle },
    pending: { tone: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30", title: "In review", body: profile?.kyc_submitted_at ? `Submitted ${new Date(profile.kyc_submitted_at).toLocaleString("en-GB")}. We typically respond within 24 hours.` : "We typically respond within 24 hours.", icon: Clock },
    verified: { tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30", title: "Verified", body: "Your account is fully verified. You appear in customer search and instant-book.", icon: CheckCircle2 },
    rejected: { tone: "bg-destructive/10 text-destructive border border-destructive/30", title: "Needs attention", body: profile?.kyc_reviewer_notes ?? "Please update the highlighted documents and resubmit.", icon: AlertCircle },
  };
  const cfg = map[status] ?? map.unverified;
  const Icon = cfg.icon;
  return (
    <div className={`mt-4 flex items-start gap-3 rounded-2xl px-4 py-3 text-sm ${cfg.tone}`}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0">
        <p className="font-semibold">{cfg.title}</p>
        <p className="opacity-80">{cfg.body}</p>
      </div>
    </div>
  );
}
